import React from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useTokenStore } from '../../storage/tokenState';
import { useUserStore } from '../../storage/userState';
import { NAVIGATION_ROUTES } from '../../constant';
import { getPendingKycSession, isKycApproved } from './helper';
import styles from './PendingKycBanner.module.css';

/** Full-screen experiences where a floating banner would be in the way. */
const SUPPRESSED_ROUTES = [
  NAVIGATION_ROUTES.SPLASH,
  NAVIGATION_ROUTES.KYC,
  NAVIGATION_ROUTES.SIGNIN,
  NAVIGATION_ROUTES.OTP,
  NAVIGATION_ROUTES.GAMEPLAY,
  NAVIGATION_ROUTES.WINNER_SCREEN,
];

interface IPendingKycBanner {
  /**
   * Live pending state from `useCompletePendingKyc`, when the host screen runs
   * completion itself. Omitted, the banner reads storage on its own.
   */
  hasPending?: boolean;
  /** Suppresses a second click while an attempt is in flight. */
  isCompleting?: boolean;
  /**
   * Retries completion in place. Given one, the CTA finishes the verification on
   * the screen the user is already on instead of routing them to /kyc to press
   * another button.
   */
  onRetry?: () => void;
}

/**
 * A standing reminder that a verification was started and never finished.
 *
 * Necessary because the client is the only record that it happened: with no
 * SurePass webhook, an unfinished verification is finished by the user coming
 * back and confirming, or not at all. The retry affordance used to live only on
 * /kyc and /wallet, so a user who wandered anywhere else had no route back to a
 * DigiLocker session they had already completed.
 *
 * Reads storage on every render rather than holding state: the pending session
 * is written by other screens and by the popup bridge, and a stale copy here
 * would either hide a live one or advertise a finished one.
 */
const PendingKycBanner: React.FC<IPendingKycBanner> = ({
  hasPending,
  isCompleting,
  onRetry,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token: userToken } = useTokenStore();
  // Subscribed for the re-render, not the value: `userDetails` is refreshed
  // every time a verification resolves, which is exactly when this banner needs
  // to re-read storage. Route changes cover the rest.
  const { userDetails } = useUserStore();

  const isSuppressed = SUPPRESSED_ROUTES.some(
    route => location.pathname === route || location.pathname.startsWith(`${route}/`),
  );

  if (isSuppressed) return null;
  if (isKycApproved(userDetails)) return null;
  // Shown logged out too: a parked session with no token is precisely the user
  // who needs telling that signing back in is what finishes their verification.
  // The CTA routes through /kyc, whose guard carries them to sign-in and back.
  if (!(hasPending ?? Boolean(getPendingKycSession()))) return null;

  return (
    <div className={styles.banner} role="status">
      <p className={clsx(styles.text, 'fontSizeSm12')}>
        {t('kycPendingBannerText')}
      </p>
      <button
        type="button"
        className={clsx(styles.cta, 'fontSizeSm12 fontWeightBold')}
        disabled={Boolean(isCompleting)}
        onClick={() => {
          if (onRetry && userToken?.accessToken) {
            onRetry();
            return;
          }
          navigate(NAVIGATION_ROUTES.KYC, {
            state: { returnTo: `${location.pathname}${location.search}` },
          });
        }}
      >
        {isCompleting ? t('pleaseWait') : t('kycPendingBannerCta')}
      </button>
    </div>
  );
};

export default PendingKycBanner;
